import { PromoCode } from "@/types/admin";
import { format } from "date-fns";
import { MoreHorizontal, Edit, Trash2, CheckCircle, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface PromoCodeTableProps {
  promoCodes: PromoCode[];
  loading: boolean;
  onDeactivate: (id: string) => void;
  onActivate: (id: string) => void;
}

export function PromoCodeTable({ promoCodes, loading, onDeactivate, onActivate }: PromoCodeTableProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-black border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border-2 border-black bg-white shadow-[4px_4px_0_#000]">
      <table className="w-full text-left">
        <thead className="bg-[#FFD93D] border-b-2 border-black">
          <tr>
            <th className="px-6 py-4 font-black">{t('admin.promoCodes.code')}</th>
            <th className="px-6 py-4 font-black">{t('admin.promoCodes.type')}</th>
            <th className="px-6 py-4 font-black">{t('admin.promoCodes.value')}</th>
            <th className="px-6 py-4 font-black">{t('admin.promoCodes.uses')}</th>
            <th className="px-6 py-4 font-black">{t('admin.promoCodes.status')}</th>
            <th className="px-6 py-4 font-black">{t('admin.promoCodes.expiresAt')}</th>
            <th className="px-6 py-4 font-black text-right">{t('admin.content.actions.view')}</th>
          </tr>
        </thead>
        <tbody className="divide-y-2 divide-black/5">
          {promoCodes.map((promo) => {
            const isActive = promo.is_active;
            const isExpired = promo.expires_at && new Date(promo.expires_at) < new Date();
            const isFull = promo.max_uses && promo.used_count >= promo.max_uses;
            
            return (
              <tr key={promo.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-bold">{promo.code}</td>
                <td className="px-6 py-4 text-sm">
                  {t(`admin.promoCodes.form.types.${promo.discount_type}`)}
                </td>
                <td className="px-6 py-4 font-bold">
                  {promo.discount_type === 'fixed' && `$${promo.discount_value}`}
                  {promo.discount_type === 'percentage' && `${promo.discount_value}%`}
                  {promo.discount_type === 'free_month' && '100%'}
                </td>
                <td className="px-6 py-4 text-sm">
                  {promo.used_count} / {promo.max_uses || '∞'}
                </td>
                <td className="px-6 py-4">
                  <span className={cn(
                    "inline-flex rounded-full border-2 border-black px-2 py-0.5 text-xs font-bold",
                    isActive && !isExpired && !isFull ? "bg-[#6FEDD6]" : "bg-red-100"
                  )}>
                    {isActive ? (isExpired ? t('admin.promoCodes.expired') : isFull ? t('admin.promoCodes.full') : t('admin.promoCodes.activeStatus')) : t('admin.promoCodes.form.inactive')}
                  </span>
                </td>
                <td className="px-6 py-4 text-xs text-gray-500">
                  {promo.expires_at ? format(new Date(promo.expires_at), 'yyyy-MM-dd') : t('admin.promoCodes.neverExpires')}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Link 
                      to={`/admin/promo-codes/${promo.id}`}
                      className="rounded-lg border-2 border-black bg-white p-2 hover:bg-[#6FEDD6] hover:shadow-[2px_2px_0_#000]"
                    >
                      <Edit className="h-4 w-4" />
                    </Link>
                    <button 
                      onClick={() => isActive ? onDeactivate(promo.id) : onActivate(promo.id)}
                      className={cn(
                        "rounded-lg border-2 border-black p-2 hover:shadow-[2px_2px_0_#000]",
                        isActive ? "bg-red-50 hover:bg-red-100" : "bg-green-50 hover:bg-green-100"
                      )}
                    >
                      {isActive ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
